import {
  Component,
  inject,
  signal,
  computed,
  ViewChild,
  ElementRef,
  effect,
  OnDestroy,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {
  DirectoryType,
  UsersService,
  UserType,
  Login,
  VerificationService,
} from '@on-the-go-web/sdk';
import {Router, RouterModule} from '@angular/router';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {LOGO_PATH_TOKEN} from '@on-the-go-web/library';
import {OtpInputComponent} from '@on-the-go-web/library/components/otp-input/otp-input';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {HttpContext} from '@angular/common/http';
import {SKIP_GLOBAL_ERROR_HANDLING} from '@on-the-go-web/helpers/skip-error.context';
import {AuthService} from '@on-the-go-web/helpers/services/auth.service';
import {emailValidator, EventTrackService} from '@on-the-go-web/library/helpers';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {of, interval, Subject, timer} from 'rxjs';
import {
  first,
  catchError,
  takeWhile,
  startWith,
  switchMap,
  filter,
  takeUntil,
  take,
} from 'rxjs/operators';
import {toSignal} from '@angular/core/rxjs-interop';

@Component({
  selector: 'otg-register',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    TranslatePipe,
    MatProgressSpinnerModule,
    OtpInputComponent,
  ],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent implements OnDestroy {
  private _usersService = inject(UsersService);
  private _verificationService = inject(VerificationService);
  private _route = inject(Router);
  private _track = inject(EventTrackService);
  private _snackBar = inject(MatSnackBar);
  private _authService = inject(AuthService);
  private _translate = inject(TranslateService);
  public logoPath$ = inject(LOGO_PATH_TOKEN);
  private stopPolling$ = new Subject<void>();

  @ViewChild('passwordInput') passwordInput?: ElementRef<HTMLInputElement>;
  @ViewChild(OtpInputComponent) otpInput?: OtpInputComponent;

  registerForm = new FormGroup({
    email: new FormControl<string>('', [Validators.required, emailValidator()]),
    password: new FormControl<string>(''),
  });

  // Verification state
  emailVerificationId = signal<string | null>(null);
  emailVerifying = signal<boolean>(false);
  emailError = signal<string | null>(null);
  emailResendCountdown = signal<number>(0);

  showEmailOtp = signal<boolean>(false);
  emailOtpCode = signal<string>('');

  // Lazy registration state
  accountExists = signal<boolean>(false);
  showPasswordLogin = signal<boolean>(false);
  loginLoading = signal<boolean>(false);
  hidePassword = signal<boolean>(true);

  constructor() {
    // Auto-focus logic
    effect(() => {
      if (this.showPasswordLogin()) {
        setTimeout(() => {
          this.passwordInput?.nativeElement.focus();
        }, 100);
      }
    });

    effect(() => {
      if (this.showEmailOtp()) {
        setTimeout(() => {
          this.otpInput?.focus();
        }, 100);
      }
    });
  }

  ngOnDestroy(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
  }
  private pollVerificationStatus(id: string) {
    timer(15000, 15000)
      .pipe(
        take(3),
        takeUntil(this.stopPolling$),
        takeWhile(() => this.showEmailOtp()),
        switchMap(() => this._verificationService.getVerifyStatus(id)),
        filter((res: any) => res.status === 'FAILED'),
      )
      .subscribe(() => {
        this._track.trackEmailDeliveryFailed('register');
        this.emailError.set(
          this._translate.instant('emailBounceError') ||
            'Email delivery failed. Please check the address and try again.',
        );
        this.showEmailOtp.set(false);
        this.emailVerifying.set(false);
        this.stopPolling$.next();
      });
  }

  // Track email control state to make computed signal reactive
  private _emailValue = toSignal(
    this.registerForm
      .get('email')!
      .valueChanges.pipe(startWith(this.registerForm.get('email')?.value)),
  );
  private _emailStatus = toSignal(
    this.registerForm
      .get('email')!
      .statusChanges.pipe(startWith(this.registerForm.get('email')?.status)),
  );

  // Computed state
  isEmailValid = computed(() => {
    // Accessing these signals makes the computed reactive
    this._emailValue();
    this._emailStatus();
    const emailControl = this.registerForm.get('email');
    return emailControl?.valid && !!emailControl.value;
  });

  onSubmit() {
    if (!this.showEmailOtp() && !this.showPasswordLogin()) {
      this.checkEmailVerification();
    } else if (this.showPasswordLogin()) {
      this.loginWithPassword();
    } else if (this.showEmailOtp()) {
      this.verifyEmailOtp(this.emailOtpCode());
    }
  }

  checkEmailVerification() {
    if (!this.isEmailValid()) {
      this.registerForm.get('email')?.markAsTouched();
      return;
    }

    const emailValue = this.registerForm.get('email')?.value;
    if (!emailValue) return;

    this.resetEmailAuth();
    this.emailVerifying.set(true);

    this._usersService
      .userTypePost({email: emailValue})
      .pipe(first())
      .subscribe({
        next: (response) => {
          this.emailVerifying.set(false);
          if (response?.type === UserType.OtgPasswordless) {
            // Existing passwordless user → OTP login
            this._track.trackAuthCheck('register', 'otp');
            this.sendEmailOtp();
          } else if (response?.type) {
            // User with password
            this._track.trackAuthCheck('register', 'password');
            this.accountExists.set(true);
            this.showPasswordLogin.set(true);
          } else {
            // New user → register first, then send OTP
            this._track.trackAuthCheck('register', 'new');
            this.registerAndSendOtp(emailValue);
          }
        },
        error: () => {
          this.sendEmailOtp();
        },
      });
  }

  private registerAndSendOtp(email: string): void {
    this.emailVerifying.set(true);
    this._usersService
      .userRegisterPost(
        {
          email,
          directory: window?.APP_CONFIG?.WHITELABEL === 'paro' ? DirectoryType.Pa : undefined,
        },
        'body',
        false,
        {
          context: new HttpContext().set(SKIP_GLOBAL_ERROR_HANDLING, true),
        },
      )
      .pipe(
        first(),
        catchError((err) => {
          this._track.trackRegister(
            'register',
            false,
            err?.error?.error ?? err?.status?.toString(),
          );
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res !== null) {
          this._track.trackRegister('register', true);
        }
        this.sendEmailOtp();
      });
  }

  sendEmailOtp() {
    const emailValue = this.registerForm.get('email')?.value;
    if (!emailValue) return;

    this.emailVerifying.set(true);
    this.emailError.set(null);

    this._usersService
      .userOtpLoginPost({email: emailValue})
      .pipe(first())
      .subscribe({
        next: (response) => {
          this._track.trackOtpSent('register', 'email', true);
          this.showPasswordLogin.set(false);
          this.accountExists.set(false);
          this.emailVerificationId.set(response?.id ?? null);
          this.showEmailOtp.set(true);
          this.emailVerifying.set(false);
          this.startResendCountdown();
          if (response?.id) {
            this.pollVerificationStatus(response.id);
          }
        },
        error: (error) => {
          this._track.trackOtpSent(
            'register',
            'email',
            false,
            error?.error?.error ?? error?.status?.toString(),
          );
          this.emailError.set(error?.error?.error ?? this._translate.instant('failedToSendCode'));
          this.emailVerifying.set(false);
        },
      });
  }

  loginWithPassword() {
    const email = this.registerForm.get('email')?.value;
    const password = this.registerForm.get('password')?.value;

    if (!email || !password || this.registerForm.get('password')?.invalid) {
      this.registerForm.get('password')?.markAsTouched();
      return;
    }

    this.loginLoading.set(true);
    this.emailError.set(null);

    this._usersService
      .userLoginPost({email, password})
      .pipe(first())
      .subscribe({
        next: (result: any) => {
          if (result.access_token) {
            this._track.trackLogin('register', 'password', true);
            this._authService.setAuthData(result.access_token, result.userType);
            this._route.navigate(['/'], {replaceUrl: true});
          } else {
            this.loginLoading.set(false);
          }
        },
        error: (error: any) => {
          this._track.trackLogin(
            'register',
            'password',
            false,
            error?.error?.error ?? error?.status?.toString(),
          );
          this.emailError.set(error?.error?.error || this._translate.instant('loginError'));
          this.loginLoading.set(false);
        },
      });
  }

  verifyEmailOtp(code: string) {
    const verificationId = this.emailVerificationId();
    if (!verificationId) return;

    this.emailVerifying.set(true);
    this.emailError.set(null);

    this._usersService
      .userOtpLoginCompletePost({id: verificationId, code})
      .pipe(first())
      .subscribe({
        next: (response: Login) => {
          if (response.access_token) {
            this._track.trackOtpVerified('register', 'email', true);
            this._track.trackLogin('register', 'otp', true);
            this._authService.setAuthData(response.access_token, UserType.OtgPasswordless);
            this._route.navigate(['/'], {replaceUrl: true});
          } else {
            this._track.trackOtpVerified('register', 'email', false, 'invalid_code');
            this.emailError.set(this._translate.instant('invalidVerificationCode'));
            this.emailVerifying.set(false);
          }
        },
        error: (error) => {
          this._track.trackOtpVerified(
            'register',
            'email',
            false,
            error?.error?.error ?? error?.status?.toString(),
          );
          this.emailError.set(error?.error?.error ?? this._translate.instant('verificationFailed'));
          this.emailVerifying.set(false);
        },
      });
  }

  private startResendCountdown() {
    this.emailResendCountdown.set(60);
    interval(1000)
      .pipe(takeWhile(() => this.emailResendCountdown() > 0))
      .subscribe(() => {
        this.emailResendCountdown.update((v) => v - 1);
      });
  }

  resetEmailAuth() {
    this.showEmailOtp.set(false);
    this.showPasswordLogin.set(false);
    this.accountExists.set(false);
    this.emailVerificationId.set(null);
    this.emailError.set(null);
    this.emailVerifying.set(false);
    this.emailOtpCode.set('');
  }
}
